import { useMemo, useState, type ComponentProps } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PrivateLayout from '@/components/layout/auth/PrivateLayout';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ClientDetailSheet } from '@/lib/dashboard/clients/ClientDetailSheet';
import { AdvisorDetailSheet } from '@/lib/dashboard/advisors/AdvisorDetailSheet';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import { useStripeReturnFeedback } from '@/hooks/useStripeReturnFeedback';
import { useChosenItineraryPreview } from '@/lib/requests/hooks/useChosenItineraryPreview';
import { useCreateItineraryPaymentLink } from '@/lib/builder/hooks/useItineraryPayment';
import { PaymentPlanEditorModal } from '@/lib/builder/components/payment-plan/PaymentPlanEditorModal';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';
import { PaymentsFilters } from '@/lib/payments/components/PaymentsFilters';
import { PaymentsKpiRow } from '@/lib/payments/components/PaymentsKpiRow';
import { DueInstallmentsCard } from '@/lib/payments/components/DueInstallmentsCard';
import { RecentPaymentsCard } from '@/lib/payments/components/RecentPaymentsCard';
import { UnconfirmedSummary } from '@/lib/payments/components/UnconfirmedSummary';
import { PaymentsItinerariesCard } from '@/lib/payments/components/PaymentsItinerariesCard';
import {
    useMarkInstallmentPaidFromDashboard,
    useSendInstallmentReminder,
    usePaymentsDashboardError,
    usePaymentsDashboardQuery,
    PAYMENTS_FILTER_OPTIONS_QUERY_KEY,
} from '@/lib/payments/hooks/usePaymentsDashboard';
import { usePaymentsFacets } from '@/lib/payments/hooks/usePaymentsFacets';
import {
    DEFAULT_DUE_WINDOW,
    DEFAULT_PAGE_SIZE,
    DUE_WINDOW_OPTIONS,
} from '@/lib/payments/models/payments-dashboard.model';
import { PaymentsFacetSelection } from '@/lib/payments/models/payments-filters.model';


/**
 * Quanti viaggi mostrare al cliente: i prossimi, non tutti. Erano 3 quando la card stava
 * sopra le liste; nella scheda sua ne stanno fino a 5 per riga, e il carosello fa il resto.
 */
const TRAVELER_ITINERARIES_LIMIT = 12;

/**
 * Quale lista e' a schermo. Dal 27/09/2026 le liste stanno sempre a schede, anche da
 * desktop: con la terza (pagamenti da confermare) affiancarle non era piu' leggibile, e
 * la scheda aperta si prende tutta l'altezza.
 *
 * "Viaggi in programma" e' una scheda del solo cliente dal 02/10/2026: prima stava sopra
 * le liste, sempre a schermo, e da sola si mangiava mezza pagina.
 */
type VisibleList = 'due' | 'unconfirmed' | 'recent' | 'trips';
const VISIBLE_LISTS: readonly VisibleList[] = ['due', 'unconfirmed', 'recent', 'trips'];

/**
 * Dashboard pagamenti.
 *
 * Sola lettura piu' le azioni che esistono gia' altrove, tutte riusate senza copiarle:
 * registrare un incasso (mark_installment_paid, la stessa della card nel builder), pagare
 * (create-itinerary-payment-link, la stessa della modale del viaggiatore) e modificare il
 * piano (PaymentPlanEditorModal, la stessa modale dello step Checkout).
 *
 * Si apre sempre il piano **intero**, anche partendo da una singola rata: gli importi
 * devono quadrare col totale dell'itinerario, e una riga modificata da sola sfonderebbe
 * la quadratura senza mostrare dove.
 *
 * La visibilita' non e' decisa in questa pagina: la RPC filtra con can_view_itinerary(),
 * la stessa funzione delle policy RLS. Il ruolo qui serve solo a scegliere quali colonne
 * e quali pulsanti mostrare.
 */
const PaymentsPage = () => {
    const { t } = useI18n();
    const money = useMoneyCopy();
    const { userRole, userData, loading: authLoading } = useAuth();

    /**
     * Scheda cliente o consulente aperta dal pallino di una rata, come dalla Panoramica e
     * dal dettaglio di una richiesta. Una sola alla volta: aprendone una si chiude l'altra,
     * perche' il pannello e' lo stesso posto e due sovrapposti nasconderebbero il primo.
     */
    const [clientKey, setClientKey] = useState<string | null>(null);
    const [advisorId, setAdvisorId] = useState<string | null>(null);
    const [clientPeriodOnly, setClientPeriodOnly] = useState(false);
    const [advisorPeriodOnly, setAdvisorPeriodOnly] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const isTraveler = userRole === 'traveler';
    // Chi gestisce le rate — modifica del piano, solleciti: gli stessi che passano
    // can_manage_itinerary().
    const canManage =
        userRole === 'admin' ||
        userRole === 'agency' ||
        userRole === 'travel_designer' ||
        userRole === 'travel_advisor_member';
    // Registrare un incasso a mano e' tornato a loro il 02/09/2026, dopo la parentesi
    // admin-only del 26/08. Resta vero che e' una scrittura contabile che nessuno verifica
    // contro Stripe, ma ora non e' piu' gratuita: apre una commissione a debito con sopra
    // il nome di chi l'ha dichiarata, e gli admin ne ricevono notifica. Il presidio si e'
    // spostato dal permesso alla tracciabilita'. Il guard vero sta in
    // `mark_installment_paid` (can_manage_itinerary): qui si nasconde solo cio' che
    // verrebbe comunque rifiutato.
    const canMarkPaid = canManage;
    // La colonna "collaboratore" e' per chi ne vede piu' di uno: admin e agenzia. Advisor
    // e membri di agenzia vedono solo le proprie rate, quindi ripeterebbe il loro nome su
    // ogni riga.
    const showDesigner = userRole === 'admin' || userRole === 'agency';

    // Finestra, filtri e pagina stanno nella URL: la pagina resta condivisibile e il
    // ritorno dal builder non azzera il lavoro di filtratura.
    // Il parametro va controllato come stringa prima di convertirlo: Number(null) e' 0, e
    // 0 e' l'opzione "tutto il periodo": senza il null-check la pagina si aprirebbe senza
    // filtro invece che sui 30 giorni di default.
    const windowParam = searchParams.get('window');
    const dueWindowDays =
        windowParam !== null && DUE_WINDOW_OPTIONS.includes(Number(windowParam) as never)
            ? Number(windowParam)
            : DEFAULT_DUE_WINDOW;

    const selection: PaymentsFacetSelection = useMemo(
        () => ({
            itineraryId: searchParams.get('itinerary'),
            clientKey: searchParams.get('client'),
            designerId: searchParams.get('designer'),
        }),
        [searchParams]
    );

    const duePage = Math.max(1, Number(searchParams.get('duePage')) || 1);
    const itinerariesPage = Math.max(1, Number(searchParams.get('tripsPage')) || 1);
    const unconfirmedPage = Math.max(1, Number(searchParams.get('unconfirmedPage')) || 1);
    const recentPage = Math.max(1, Number(searchParams.get('recentPage')) || 1);

    // La scheda aperta sta nella URL come i filtri: il link a un collega porta anche quella.
    // "Da confermare" e' per chi gestisce le rate: al cliente la scheda non compare.
    // "Viaggi in programma", al contrario, c'e' solo per il cliente.
    const listParam = searchParams.get('list') as VisibleList | null;
    const visibleList: VisibleList =
        listParam &&
        VISIBLE_LISTS.includes(listParam) &&
        !(listParam === 'unconfirmed' && isTraveler) &&
        !(listParam === 'trips' && !isTraveler)
            ? listParam
            : 'due';

    const updateParams = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
        }
        setSearchParams(next, { replace: true });
    };

    /**
     * Cambiare un filtro riporta entrambe le liste a pagina 1: restare a pagina 4 di un
     * insieme che si e' appena ristretto a due pagine mostrerebbe una tabella vuota, e
     * sembrerebbe che il filtro non abbia trovato nulla.
     */
    const applySelection = (next: PaymentsFacetSelection) =>
        updateParams({
            itinerary: next.itineraryId,
            client: next.clientKey,
            designer: next.designerId,
            duePage: null,
            tripsPage: null,
            unconfirmedPage: null,
            recentPage: null,
        });

    const facets = usePaymentsFacets(selection, applySelection);

    const params = useMemo(
        () => ({
            dueWindowDays,
            designerId: selection.designerId,
            itineraryId: selection.itineraryId,
            clientKey: selection.clientKey,
            recentPage,
            duePage,
            itinerariesPage,
            unconfirmedPage,
            pageSize: DEFAULT_PAGE_SIZE,
            // Le schede viaggio le disegna solo il cliente, e gliene bastano poche: sono
            // un promemoria dei suoi viaggi, non un elenco da sfogliare. Agli altri ruoli
            // la card non compare affatto, quindi si chiede il minimo che la RPC accetta.
            itinerariesPageSize: isTraveler ? TRAVELER_ITINERARIES_LIMIT : 1,
        }),
        [dueWindowDays, selection, duePage, itinerariesPage, unconfirmedPage, recentPage, isTraveler]
    );

    const { data, isFetching, dataUpdatedAt, error, refetch } = usePaymentsDashboardQuery(params);
    usePaymentsDashboardError(error);

    const queryClient = useQueryClient();

    /**
     * Il refresh manuale butta via anche le opzioni dei filtri, non solo i dati.
     *
     * Le opzioni restano in cache dieci minuti perche' cambiano di rado, ma un piano creato
     * da un collega in questo momento non le invalida: e' proprio il caso in cui si preme
     * refresh, e se il pulsante non le toccasse il viaggio nuovo resterebbe fuori dalle
     * tendine senza alcun modo di farlo comparire.
     */
    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: [PAYMENTS_FILTER_OPTIONS_QUERY_KEY] });
        refetch();
    };

    // Rientro da Stripe Checkout: e' qui che tornano sia il saldo unico sia la singola
    // rata pagata dalla piattaforma. Il refetch serve perche' nel frattempo il webhook
    // puo' aver gia' marcato la rata.
    useStripeReturnFeedback({ onSuccess: refetch });

    // Il titolo del viaggio apre la modale di dettaglio, come l'itinerario scelto nella
    // pagina richieste: dal builder si passa dal suo pulsante, non con un click sul nome.
    const { openPreview: openItinerary } = useChosenItineraryPreview();

    const markPaid = useMarkInstallmentPaidFromDashboard();
    const createPaymentLink = useCreateItineraryPaymentLink();
    const sendReminder = useSendInstallmentReminder();

    /**
     * Rate sollecitate da quando la pagina e' aperta. Serve solo a disattivare il pulsante:
     * il vincolo vero e' il throttle di un'ora nella RPC, che vale anche fra sessioni e
     * fra utenti diversi della stessa agenzia.
     */
    const [remindedIds, setRemindedIds] = useState<Set<string>>(new Set());

    /**
     * Rata per cui e' in corso l'apertura del checkout, o null.
     *
     * Resta valorizzata anche a mutation riuscita: `onSuccess` fa `window.location.href`,
     * e la navigazione non e' istantanea. Guardando solo `isPending` il pulsante tornerebbe
     * "Paga" per un istante prima che la pagina cambi, proprio mentre l'utente aspetta.
     */
    const payingInstallmentId =
        createPaymentLink.isPending || createPaymentLink.isSuccess
            ? createPaymentLink.variables?.installmentId ?? null
            : null;

    /**
     * Itinerario di cui e' aperto l'editor del piano. Una sola istanza della modale per
     * tutta la pagina: e' la stessa operazione da qualunque riga la si apra, e montarne
     * una per riga vorrebbe dire una query del piano per ogni viaggio a schermo.
     *
     * Il totale non serve passarlo: la modale usa `plan.itinerary_total`, che la RPC legge
     * dal database. Qui non c'e' un itinerario aperto in memoria da cui prenderlo.
     */
    const [planToEdit, setPlanToEdit] = useState<{
        itineraryId: string;
        installmentId?: string;
    } | null>(null);

    /*
     * Le liste mostrano lo skeleton per tutto il fetch (primo accesso, pagina, filtri,
     * aggiorna): la query tiene a schermo il risultato precedente, ma righe vecchie sotto
     * filtri nuovi sembrano gia' la risposta. Al primo accesso il resto della pagina e'
     * gia' montato -- filtri, schede, KPI a scheletro -- invece di uno spinner al centro.
     * `dataUpdatedAt` cambia a ogni risposta: e' la chiave che fa rientrare le righe.
     */
    const listLoading = authLoading || isFetching || !data;
    const listVersion = dataUpdatedAt;

    /*
     * "In scadenza" e "Da confermare" sono la stessa tabella: le righe hanno la stessa
     * forma (una rata aperta) e le stesse azioni. Nella seconda "segna come pagata" e' la
     * conferma manuale della proposta: il primo incasso la porta a completed. Le props
     * comuni stanno qui una volta sola perche' le due schede non divergano.
     */
    const installmentsCardProps: Omit<ComponentProps<typeof DueInstallmentsCard>, 'rows'> = {
        people: data?.people ?? {},
        dueWindowDays: data?.due_window_days ?? dueWindowDays,
        isLoading: listLoading,
        animationKey: listVersion,
        canManage,
        canMarkPaid,
        canPay: isTraveler,
        travelerView: isTraveler,
        /* Al viaggiatore no: le schede raccontano il perimetro di una persona --
           pratiche, margini, scadenze altrui -- e la RPC gliele rifiuterebbe
           comunque. */
        onOpenClient: isTraveler
            ? undefined
            : (key) => {
                  setAdvisorId(null);
                  setClientKey(key);
              },
        onOpenAdvisor: isTraveler
            ? undefined
            : (id) => {
                  setClientKey(null);
                  setAdvisorId(id);
              },
        isMarkingPaid: markPaid.isPending,
        payingInstallmentId,
        isSendingReminder: sendReminder.isPending,
        remindedIds,
        onSendReminder: ({ installmentId, language }) =>
            sendReminder.mutate(
                { installmentId, language },
                {
                    onSuccess: () =>
                        setRemindedIds((prev) => new Set(prev).add(installmentId)),
                }
            ),
        onMarkPaid: (payload) => markPaid.mutate(payload),
        onPay: ({ itineraryId, installmentId }) =>
            createPaymentLink.mutate({
                itineraryId,
                installmentId,
                customerEmail: userData?.email ?? null,
            }),
        onEditPlan: (itineraryId, installmentId) =>
            setPlanToEdit({ itineraryId, installmentId }),
        showDesigner,
        onOpenItinerary: openItinerary,
    };

    const dueCard = (
        <DueInstallmentsCard
            {...installmentsCardProps}
            rows={data?.due_installments ?? []}
            page={data?.due_installments_page}
            onPageChange={(page) => updateParams({ duePage: String(page) })}
        />
    );

    const unconfirmedCard = (
        <DueInstallmentsCard
            {...installmentsCardProps}
            rows={data?.unconfirmed ?? []}
            showWindowHint={false}
            summary={<UnconfirmedSummary kpis={data?.unconfirmed_kpis} />}
            emptyMessage={t('payments.unconfirmed.empty')}
            page={data?.unconfirmed_page}
            onPageChange={(page) => updateParams({ unconfirmedPage: String(page) })}
        />
    );

    const recentCard = (
        <RecentPaymentsCard
            rows={data?.recent_paid ?? []}
            people={data?.people ?? {}}
            isLoading={listLoading}
            animationKey={listVersion}
            showDesigner={showDesigner}
            travelerView={isTraveler}
            onOpenClient={installmentsCardProps.onOpenClient}
            onOpenAdvisor={installmentsCardProps.onOpenAdvisor}
            onOpenItinerary={openItinerary}
            page={data?.recent_paid_page}
            onPageChange={(page) => updateParams({ recentPage: String(page) })}
        />
    );

    /* Le schede viaggio le vede solo il cliente: sono il promemoria dei suoi viaggi con
       un piano di pagamento. Chi lavora ha la pagina Viaggi per l'elenco completo, e le
       commissioni -- che al cliente non riguardano -- stanno nella riga dei contatori. */
    const tripsCard = (
        <div className="h-full overflow-y-auto">
            <PaymentsItinerariesCard
                rows={data?.itineraries ?? []}
                people={data?.people ?? {}}
                isLoading={listLoading}
                canManage={canManage}
                showDesigner={showDesigner}
                onEditPlan={(itineraryId) => setPlanToEdit({ itineraryId })}
                onOpenItinerary={openItinerary}
            />
        </div>
    );

    return (
        <PrivateLayout
            pageTitle={t('payments.title')}
            pageDescription={t(`${money}.pageSubtitle`)}
            /* La pagina e' alta quanto la finestra e non scorre: a scorrere sono le due
               liste, dentro le rispettive card. Filtri e KPI restano sempre a schermo. */
            contentClassName="h-full flex flex-col min-h-0 py-2 md:py-4"
        >
            <div className="flex flex-1 flex-col min-h-0 w-full md:px-4 mx-auto gap-4">
                <PaymentsFilters
                    dueWindowDays={dueWindowDays}
                    onDueWindowChange={(days) =>
                        updateParams({ window: String(days), duePage: null, tripsPage: null, unconfirmedPage: null, recentPage: null })
                    }
                    selection={selection}
                    onSelectionChange={applySelection}
                    itineraryOptions={facets.itineraryOptions}
                    clientOptions={isTraveler ? undefined : facets.clientOptions}
                    designerOptions={showDesigner ? facets.designerOptions : undefined}
                    facetQuery={facets.query}
                    onFacetQueryChange={facets.setQuery}
                    facetsTruncated={facets.truncated}
                    facetsSearching={facets.isSearching}
                    facetsTotalCount={facets.totalCount}
                    isRefreshing={listLoading}
                    onRefresh={handleRefresh}
                />

                <PaymentsKpiRow
                    kpis={data?.kpis}
                    dueWindowDays={listLoading ? dueWindowDays : (data?.due_window_days ?? dueWindowDays)}
                    isLoading={listLoading}
                />

                {/* Le liste stanno a schede a ogni larghezza: una sola a schermo, che si
                    prende tutta l'altezza e scorre dentro la propria card. */}
                <div className="flex flex-1 flex-col min-h-0 gap-3">
                    <Tabs
                        value={visibleList}
                        onValueChange={(value) =>
                            updateParams({ list: value === 'due' ? null : value })
                        }
                        className="flex-shrink-0"
                    >
                        <TabsList className="w-full">
                            <TabsTrigger value="due" className="flex-1">
                                {t('payments.due.title')}
                            </TabsTrigger>
                            {!isTraveler && (
                                <TabsTrigger value="unconfirmed" className="flex-1">
                                    {t('payments.unconfirmed.title')}
                                    {!!data?.unconfirmed_kpis?.count && (
                                        <span className="ml-1.5 tabular-nums text-gray-500">
                                            ({data.unconfirmed_kpis.count})
                                        </span>
                                    )}
                                </TabsTrigger>
                            )}
                            <TabsTrigger value="recent" className="flex-1">
                                {t(`${money}.recentTitle`)}
                            </TabsTrigger>
                            {isTraveler && (
                                <TabsTrigger value="trips" className="flex-1">
                                    {t('payments.trips.title')}
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </Tabs>

                    <div className="flex-1 min-h-0">
                        {visibleList === 'due'
                            ? dueCard
                            : visibleList === 'unconfirmed'
                              ? unconfirmedCard
                              : visibleList === 'trips'
                                ? tripsCard
                                : recentCard}
                    </div>
                </div>
            </div>

            {/* Fuori dal contenitore a scroll interno: le schede vivono in un portale
                loro e devono poter coprire la pagina, non scorrere dentro una card. */}
            <ClientDetailSheet
                clientKey={clientKey}
                onOpenChange={(open) => {
                    if (!open) {
                        setClientKey(null);
                        setClientPeriodOnly(false);
                    }
                }}
                period="30d"
                periodOnly={clientPeriodOnly}
                onPeriodOnlyChange={setClientPeriodOnly}
            />

            <AdvisorDetailSheet
                advisorId={advisorId}
                onOpenChange={(open) => {
                    if (!open) {
                        setAdvisorId(null);
                        setAdvisorPeriodOnly(false);
                    }
                }}
                period="30d"
                periodOnly={advisorPeriodOnly}
                onPeriodOnlyChange={setAdvisorPeriodOnly}
                /* Dalla scheda del consulente si salta a quella di un suo cliente, come
                   nella Panoramica: il pannello si sostituisce, non se ne apre un terzo. */
                onOpenClient={(key) => {
                    setAdvisorId(null);
                    setClientKey(key);
                }}
            />

            <PaymentPlanEditorModal
                isOpen={planToEdit !== null}
                itineraryId={planToEdit?.itineraryId}
                highlightInstallmentId={planToEdit?.installmentId}
                formatAmount={formatPlanAmount}
                onClose={() => setPlanToEdit(null)}
            />
        </PrivateLayout>
    );
};

export default PaymentsPage;
